// CoT-transport: TCP / UDP / multicast — utover CoT-over-HTTP. Dette er wire-laget
// mot TAK-økosystemet: ATAK/WinTAK oppdager hverandre via SA-multicast (Situational
// Awareness) på 239.2.3.1:6969, og TAK-servere tar imot CoT som en rå XML-strøm
// over TCP. Her ligger den RENE, testbare kjernen: endepunkt-modell, framing og
// strøm-reassembly. Selve socket-sendingen (node:dgram/net) ligger i hub-en
// (`services/hub/src/cot-udp.ts`) — den bruker disse hjelperne.
import { z } from 'zod';
/** Standard ATAK «Situational Awareness»-multicast (default i ATAK/WinTAK). */
export const TAK_SA_MULTICAST = { group: '239.2.3.1', port: 6969 };
/** Transport for CoT ut av SKADE. */
export const CotProto = z.enum(['udp', 'tcp', 'multicast']);
export const CotEndpointSchema = z.object({
    proto: CotProto,
    host: z.string().min(1),
    port: z.number().int().min(1).max(65535),
    /** Multicast TTL (hopp). Default 1 = kun lokalt segment (LPI/OPSEC-vennlig). */
    ttl: z.number().int().min(0).max(255).optional(),
});
/** Coerce vilkårlig input til et gyldig endepunkt, eller null. */
export function parseCotEndpoint(v) {
    const r = CotEndpointSchema.safeParse(v);
    return r.success ? r.data : null;
}
/** Default TAK SA-multicast-endepunkt (det ATAK lytter på ut av boksen). */
export function defaultTakMulticast() {
    return { proto: 'multicast', host: TAK_SA_MULTICAST.group, port: TAK_SA_MULTICAST.port, ttl: 1 };
}
/** Er adressen en IPv4-multicast-adresse (224.0.0.0/4)? */
export function isMulticastAddress(host) {
    const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host.trim());
    if (!m)
        return false;
    const octs = m.slice(1).map(Number);
    if (octs.some((o) => o > 255))
        return false;
    return octs[0] >= 224 && octs[0] <= 239;
}
/** Konsistens-sjekk: multicast-proto krever multicast-adresse, og omvendt. */
export function validateEndpoint(ep) {
    const mc = isMulticastAddress(ep.host);
    if (ep.proto === 'multicast' && !mc) {
        return { ok: false, reason: `multicast krever 224.0.0.0/4-adresse, fikk ${ep.host}` };
    }
    if (ep.proto !== 'multicast' && mc) {
        return { ok: false, reason: `${ep.proto} kan ikke bruke multicast-adresse ${ep.host}` };
    }
    return { ok: true };
}
/**
 * Framing for CoT over en TCP-strøm. TAK-protokoll v0 er ren XML; meldinger
 * skilles ikke av lengde-prefiks, så vi legger på en linjeskift-terminator for
 * lesbarhet og robust reassembly (`parseCotStream` deler uansett på </event>).
 */
export function frameCotStream(xml) {
    return xml.endsWith('\n') ? xml : xml + '\n';
}
/**
 * Reassembler en (potensielt fragmentert) TCP-strøm til hele CoT-events.
 * Returnerer komplette `<event …>…</event>`-dokumenter + `rest` (ufullstendig
 * hale som skal settes foran neste chunk). Ekte strøm-håndtering — en TCP-lesing
 * kan gi et halvt event, og neste lesing resten.
 */
export function parseCotStream(buffer) {
    const events = [];
    const close = '</event>';
    let idx = 0;
    for (;;) {
        const start = buffer.indexOf('<event', idx);
        if (start < 0)
            break;
        const end = buffer.indexOf(close, start);
        if (end < 0) {
            idx = start;
            break;
        }
        events.push(buffer.slice(start, end + close.length));
        idx = end + close.length;
    }
    return { events, rest: buffer.slice(idx) };
}
//# sourceMappingURL=cot-transport.js.map